import { useEffect, useRef, useState } from 'react'
import { Reveal } from '../../../components/Reveal.jsx'
import { EyebrowLabel } from '../../../components/EyebrowLabel.jsx'

const quotes = [
  {
    quote:
      'After years of perimenopause symptoms, my provider actually listened and built a real protocol. I feel like myself again.',
    name: 'Sarah M.',
    treat: "Women's Hormones · Austin, TX",
    initial: 'S',
    tone: 'bg-lagoon',
  },
  {
    quote:
      'I was skeptical about online TRT, but it was thorough — labs, real provider review, ongoing check-ins. My energy is transformed.',
    name: 'James R.',
    treat: "Men's Hormones · Chicago, IL",
    initial: 'J',
    tone: 'bg-teal-600',
  },
  {
    quote:
      "The monitoring made all the difference. This wasn't a vending-machine prescription — my care team was with me the whole way.",
    name: 'Alicia T.',
    treat: 'GLP-1 · Denver, CO',
    initial: 'A',
    tone: 'bg-plum-600',
  },
  {
    quote:
      'Knowing my peptides come from a real 503A pharmacy with a real prescription is exactly why I chose HealBerry. Recovery has genuinely improved.',
    name: 'Marcus D.',
    treat: 'Peptides · Miami, FL',
    initial: 'M',
    tone: 'bg-leaf-500',
  },
]

/**
 * A card's left edge in the scroller's scroll coordinates. Not `offsetLeft`:
 * each card's offsetParent is its own Reveal wrapper, so offsetLeft was 0 for
 * every card — the dots never scrolled and the active dot never moved.
 */
function cardLeft(scroller, card) {
  return card.getBoundingClientRect().left - scroller.getBoundingClientRect().left + scroller.scrollLeft
}

export function Testimonials() {
  const scrollerRef = useRef(null)
  const [active, setActive] = useState(0)

  useEffect(() => {
    const el = scrollerRef.current
    if (!el) return undefined

    function onScroll() {
      const cards = el.querySelectorAll('[data-quote-card]')
      if (!cards.length) return
      const mid = el.scrollLeft + el.clientWidth / 2
      let best = 0
      let bestDist = Infinity
      cards.forEach((card, i) => {
        const center = cardLeft(el, card) + card.offsetWidth / 2
        const dist = Math.abs(center - mid)
        if (dist < bestDist) {
          bestDist = dist
          best = i
        }
      })
      setActive(best)
    }

    // No initial onScroll(): the scroller mounts at scrollLeft 0, so `active`
    // is already 0, and measuring cards during mount forces a layout.
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [])

  function goTo(i) {
    const el = scrollerRef.current
    const card = el?.querySelectorAll('[data-quote-card]')?.[i]
    if (!el || !card) return
    el.scrollTo({ left: cardLeft(el, card) - 8, behavior: 'smooth' })
  }

  return (
    <section className="bg-[#F7FAE8] py-[104px] max-[640px]:py-14">
      <div className="hb-wrap">
        <div className="mx-auto mb-[60px] max-w-[600px] text-center max-[640px]:mb-8">
          <Reveal>
            <EyebrowLabel>Patient Stories</EyebrowLabel>
            <h2 className="mt-4 font-serif text-[clamp(32px,4.2vw,50px)] font-semibold leading-[1.08] tracking-[-0.5px] text-[#191A17]">
              Real patients. <em className="hl text-teal-600">Real</em> supervision.
            </h2>
            <p className="mt-[18px] text-[18px] text-[#6B6B6B] max-[640px]:mt-3 max-[640px]:text-[15px]">
              All testimonials are from consented HealBerry patients. Results may vary.
            </p>
          </Reveal>
        </div>
        <div
          ref={scrollerRef}
          className="flex snap-x snap-mandatory gap-[22px] overflow-x-auto scroll-strip pb-2 pr-[18%] max-[640px]:gap-3.5 max-[640px]:pr-[22%]"
        >
          {quotes.map((q, i) => (
            <Reveal key={q.name} delay={i * 60}>
              <div
                data-quote-card
                className="flex w-[360px] shrink-0 snap-start flex-col rounded-[20px] border-[1.5px] border-[#E8F1F0] bg-white p-[30px] shadow-[0_2px_16px_rgba(0,128,128,0.07)] max-[640px]:w-[280px] max-[640px]:p-5"
              >
                <div className="mb-3.5 text-[17px] tracking-[2px] text-leaf-500">★★★★★</div>
                <p className="mb-5 flex-1 text-[15.5px] italic leading-[1.75] text-[#4A4A4A]">
                  &ldquo;{q.quote}&rdquo;
                </p>
                <div className="flex items-center gap-3">
                  <span
                    className={`grid h-[46px] w-[46px] place-items-center rounded-full font-serif text-[18px] font-semibold text-white ${q.tone}`}
                  >
                    {q.initial}
                  </span>
                  <div>
                    <div className="font-display text-[14px] font-extrabold text-[#191A17]">{q.name}</div>
                    <div className="font-display text-[12px] font-semibold text-teal-600">{q.treat}</div>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        {/* Each dot sits in a 24px-tall, ≥24px-wide button (WCAG 2.5.8 target
            size) — the bare 8px dots were too small to tap reliably. The padding
            replaces the old gap, so the dots still read as a tight row. */}
        <div className="mt-3 flex items-center justify-center" aria-label="Testimonial slides">
          {quotes.map((q, i) => (
            <button
              key={q.name}
              type="button"
              aria-label={`Go to testimonial ${i + 1}`}
              aria-current={active === i}
              onClick={() => goTo(i)}
              className="group p-2"
            >
              <span
                className={`block h-2 rounded-full transition-all ${
                  active === i ? 'w-6 bg-teal-600' : 'w-2 bg-teal-600/30 group-hover:bg-teal-600/50'
                }`}
              />
            </button>
          ))}
        </div>
      </div>
    </section>
  )
}
